/* eslint-disable camelcase */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath } from 'react-router-dom';
import classNames from 'classnames';

import { ErrorMessage } from '../../common';
import { AddressSelector } from './AddressSelector';
import SelfManaged from './selfManaged/index';
import { capitalizeFirstLetter } from '../../../utils';
import { usePrevious } from '../../../hooks';
import {
    checkValidPostcode,
    checkValidPostcodeWithoutFetchingPostcodeList,
    addClientAddress,
    errorMessage,
} from '../../../actions/ClientData';
import { TRootState } from '../../../reducers';
import history from '../../../history';

const errorAddress = {
    CITY_CANNOT_BE_EMPTY: 'City cannot be empty',
    ADDRESS1_CANNOT_BE_EMPTY: 'Address1 cannot be empty',
};

const viewType = {
    CLIENT_LIST: 'clientList',
    INPUT_ADDRESS: 'inputAddress',
    INPUT_POSTCODE: 'inputPostcode',
};

type TNewClientAddress = {
    line_1: string;
    line_2: string;
    postcode: string;
    post_town: string;
};

type TClientAddress = {
    address1: undefined | string;
    address2: undefined | string;
    city: undefined | string;
    postcode: undefined | string;
};

type TLocationCard = {
    address1: string;
    address2: string;
    city: string;
    postcode: string;
    updateField: (field: string, data: string) => void;
    className?: string;
    inputOnly: boolean;
    mode: string;
};

export function LocationCard({
    updateField,
    className,
    inputOnly,
    mode,
    ...props
}: TLocationCard) {
    const [address1, setAddress1] = useState(null);
    const [address2, setAddress2] = useState(null);
    const [city, setCity] = useState(null);
    const [postcode, setPostcode] = useState(null);
    const [input, setInput] = useState(viewType.CLIENT_LIST);
    const [collapsed, setCollapsed] = useState(true);
    const [error, setError] = useState(null);
    const [postcodeAddressListFormatted, setPostcodeAddressListFormatted] =
        useState(null);

    const prevPostcode = usePrevious(props.postcode);
    const dispatch = useDispatch();

    const {
        postcodeAddressList,
        clientAddressList,
        clientDataError,
        loadingPostcode,
        postcodeAddress,
        postcodeAddressEditJob,
        user,
    } = useSelector((state: TRootState) => ({
        postcodeAddressList: state.clientData.postcodeAddressList,
        clientAddressList: state.clientData.clientAddressList,
        clientDataError: state.clientData.error,
        loadingPostcode: state.loading.postcode,
        postcodeAddress: state.requestData.bookingRequest.postcode,
        postcodeAddressEditJob:
            state.editJobRequestData.originalJobData.postcode,
        user: state.user,
    }));

    const prevPostcodeAddress = usePrevious(postcodeAddress);

    let map: any;
    let geocoder: any;
    let marker: any;

    const updateFields = useCallback(
        (clientAddress: TClientAddress) => {
            updateField('address1', clientAddress.address1);
            updateField('address2', clientAddress.address2);
            updateField('city', clientAddress?.city && capitalizeFirstLetter(clientAddress.city));
            updateField('postcode', clientAddress.postcode);
        },
        [updateField],
    );

    const loadMap = useCallback(() => {
        if (map) return;

        const myOptions = {
            zoom: 12,
            center: { lat: 51.528, lng: -0.382 },
            // eslint-disable-next-line no-undef
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            disableDefaultUI: true,
            styles: [
                {
                    featureType: 'all',
                    stylers: [{ saturation: '-75' }],
                },
                {
                    featureType: 'poi',
                    stylers: [{ visibility: 'off' }],
                },
                {
                    featureType: 'administrative.locality',
                    stylers: [{ visibility: 'off' }],
                },
                {
                    featureType: 'water',
                    stylers: [
                        { hue: '0' },
                        { saturation: '40' },
                        { lightness: '-10' },
                    ],
                },
            ],
        };

        const placeholder = Array.prototype.filter.call(
            document.querySelectorAll('.map-wrapper .map'),
            (obj: { offsetWidth: number; offsetHeight: number }) =>
                obj.offsetWidth > 0 && obj.offsetHeight > 0,
        )[0];

        // eslint-disable-next-line no-undef
        map = new google.maps.Map(placeholder, myOptions);
        // eslint-disable-next-line no-undef
        geocoder = new google.maps.Geocoder();
    }, []);

    const handleSave = useCallback(() => {
        const clientAddress = {
            address1,
            address2,
            city,
            postcode,
        };

        if (!city?.trim()) {
            setError(errorAddress.CITY_CANNOT_BE_EMPTY);
        } else if (!address1?.trim()) {
            setError(errorAddress.ADDRESS1_CANNOT_BE_EMPTY);
        } else {
            updateFields(clientAddress);
            dispatch(addClientAddress(clientAddress));
            dispatch(errorMessage('postcode', null));
            setInput(viewType.CLIENT_LIST);
        }
    }, [address1, address2, city, dispatch, postcode, updateFields]);

    const handleNewAddress = () => {
        let cityName = '';

        if (!clientDataError.postcode) {
            const postTown = postcodeAddressList?.[0]?.post_town;
            cityName = city || postTown;
        }

        setAddress1(null);
        setAddress2(null);
        setCity(cityName);
        setCollapsed(true);
        setInput(viewType.INPUT_ADDRESS);
    };

    const clearState = () => {
        dispatch(errorMessage('postcode', null));

        setAddress1(null);
        setAddress2(null);
        setCity(null);
        setPostcode(null);
        setCollapsed(true);
        setError(null);
        setInput(viewType.INPUT_POSTCODE);
    };

    const handleClickCollapse = () => {
        setCollapsed(prevState => !prevState);
    };

    const handleItemSelected = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            let clientAddress: TClientAddress;

            if (e.target.getAttribute('data-type') === 'postcode') {
                clientAddress = postcodeAddressListFormatted[e.target.value];
                dispatch(addClientAddress(clientAddress));
            } else {
                clientAddress = clientAddressList[e.target.value];
                dispatch(
                    checkValidPostcode(clientAddress.postcode, () =>
                        setCollapsed(true),
                    ),
                );
            }

            if (updateField && clientAddress) {
                updateFields(clientAddress);
            }

            setInput(viewType.CLIENT_LIST);
            setCollapsed(true);
        },
        [
            clientAddressList,
            dispatch,
            postcodeAddressListFormatted,
            updateField,
            updateFields,
        ],
    );

    const changePostcode = useCallback((newPostcode: string) => {
        if (newPostcode) {
            setPostcode(newPostcode);
            dispatch(
                checkValidPostcodeWithoutFetchingPostcodeList(newPostcode),
            );
        }

        if (newPostcode !== null && map) {
            if (geocoder) {
                geocoder.geocode(
                    {
                        address: newPostcode,
                    },
                    (results: any, status: any) => {
                        if (marker) marker.setMap(null);
                        marker = undefined;

                        // eslint-disable-next-line no-undef
                        if (
                            status === google.maps.GeocoderStatus.OK &&
                            // eslint-disable-next-line no-undef
                            status !== google.maps.GeocoderStatus.ZERO_RESULTS
                        ) {
                            map.setCenter(results[0].geometry.location);

                            // eslint-disable-next-line no-inner-declarations
                            const customIcon = (opts: any): any => ({
                                // eslint-disable-next-line max-len
                                path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z M -2,-30 a 2,2 0 1,1 4,0 2,2 0 1,1 -4,0',
                                fillColor: '#34495e',
                                fillOpacity: 1,
                                strokeColor: '#000',
                                strokeWeight: 2,
                                scale: 1,
                                ...opts,
                            });

                            // eslint-disable-next-line no-undef
                            marker = new google.maps.Marker({
                                position: results[0].geometry.location,
                                map,
                                title: postcode,
                                icon: customIcon({
                                    fillColor: '#AB2B87',
                                }),
                            });
                        }
                    },
                );
            }
        }
    }, []);

    const handleInputPostcode = (e: React.FormEvent<HTMLInputElement>) => {
        const postcodeFromInput = e.currentTarget.value;
        const MIN_POSTCODE_LENGTH = 4;

        if (postcodeFromInput.length > MIN_POSTCODE_LENGTH) {
            changePostcode(postcodeFromInput);
            dispatch(
                checkValidPostcode(postcodeFromInput, () =>
                    setCollapsed(false),
                ),
            );
        }
    };

    useEffect(() => {
        if (!inputOnly) {
            if ((window as any).gMapLoaded) {
                loadMap();
            } else {
                (window as any).gMapCallback = loadMap;
            }
        }

        changePostcode(props.postcode);
    }, []);

    useEffect(() => {
        const postcodeAddressListFormattedMap = postcodeAddressList?.map(
            (address: TNewClientAddress) => ({
                address1: address.line_1,
                address2: address.line_2,
                postcode: address.postcode,
                city: address.post_town,
            }),
        );
        setPostcodeAddressListFormatted(postcodeAddressListFormattedMap);
    }, [postcodeAddressList]);

    useEffect(() => {
        const isEditJobPathActive = !!matchPath(
            history.location.pathname,
            '/jobs/list/edit/:id',
        );

        const properPostcode = isEditJobPathActive
            ? postcodeAddressEditJob
            : postcodeAddress;

        if (properPostcode && prevPostcodeAddress !== postcodeAddress) {
            dispatch(
                checkValidPostcodeWithoutFetchingPostcodeList(properPostcode),
            );
        }
    }, [postcodeAddress, postcodeAddressEditJob]);

    useEffect(() => {
        const initializeFields = () => {
            // eslint-disable-next-line no-shadow
            const { address1, address2, city, postcode } = props;

            if (clientAddressList?.length > 0) {
                const addressFieldsAreEmpty =
                    address1 === null &&
                    address2 === null &&
                    city === null &&
                    postcode === null;

                if (addressFieldsAreEmpty) {
                    updateFields(clientAddressList[0]);
                }
            }
        };

        initializeFields();
    }, [clientAddressList]);

    useEffect(() => {
        if (
            prevPostcode !== postcode &&
            input !== viewType.INPUT_POSTCODE &&
            prevPostcode !== null
        ) {
            changePostcode(prevPostcode);
        }
    }, [changePostcode, postcode, prevPostcode]);

    const renderDefaultInput = (hasPostcodeResults: boolean) => {
        const hasBookingTunnel =
            user?.isAgent &&
            user.agency?.features.includes('booking_tunnel') &&
            !mode;

        return (
            <div
                className={`group group--chooseaddr${
                    collapsed ? ' collapsed' : ''
                }`}
            >
                {hasBookingTunnel && (
                    <SelfManaged clientAddressList={clientAddressList} />
                )}
                <div className="input-wrapper visible-xs">
                    <input
                        type="text"
                        placeholder="Postcode"
                        onChange={handleInputPostcode}
                    />
                    {loadingPostcode && <i className="icon icon-loading-1" />}
                </div>
                <div className="input-wrapper hidden-xs">
                    <input
                        type="text"
                        placeholder="Postcode"
                        onChange={handleInputPostcode}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus={true}
                    />
                    {loadingPostcode && <i className="icon icon-loading-1" />}
                </div>
                {hasPostcodeResults && !clientDataError.postcode ? (
                    <AddressSelector
                        addressList={postcodeAddressListFormatted}
                        searchByPostcode={true}
                        itemSelected={handleItemSelected}
                    />
                ) : (
                    <div className="no-results">No results</div>
                )}
                <div className="action-wrapper">
                    <span
                        data-change={viewType.INPUT_ADDRESS}
                        onClick={handleNewAddress}
                    >
                        {hasPostcodeResults
                            ? 'Address not listed'
                            : 'Manually add address'}
                    </span>
                </div>
                {clientDataError?.postcode && (
                    <ErrorMessage message={clientDataError?.postcode} />
                )}
            </div>
        );
    };

    const renderAddressSelector = () => {
        const hasClientAddressList = clientAddressList?.length > 0;
        const hasPostcodeResults = postcodeAddressListFormatted?.length > 0;
        const hasBookingTunnel =
            user?.isAgent &&
            user.agency?.features.includes('booking_tunnel') &&
            !mode;

        if (input === viewType.CLIENT_LIST && hasClientAddressList) {
            // eslint-disable-next-line no-shadow
            const { address1, address2, city, postcode } = props;

            return (
                <div
                    className={`group group--addrlist${
                        collapsed ? ' collapsed' : ''
                    }`}
                >
                    {hasBookingTunnel && (
                        <SelfManaged clientAddressList={clientAddressList} />
                    )}
                    <div
                        className="input-wrapper"
                        onClick={handleClickCollapse}
                    >
                        {address2 ? (
                            <p>
                                {address1}
                                <br />
                                {address2}
                                <br />
                                {`${city}, ${postcode}`}
                            </p>
                        ) : (
                            <p>
                                {address1}
                                <br />
                                {`${city}, ${postcode}`}
                            </p>
                        )}
                        <span />
                    </div>
                    <AddressSelector
                        addressList={clientAddressList}
                        itemSelected={handleItemSelected}
                    />
                    <div className="action-wrapper">
                        <span
                            data-change={viewType.INPUT_POSTCODE}
                            onClick={clearState}
                        >
                            Add another address
                        </span>
                    </div>
                    {clientDataError?.postcode && (
                        <ErrorMessage message={clientDataError?.postcode} />
                    )}
                </div>
            );
        }

        if (input === 'inputAddress') {
            return (
                <div className="group group--addaddr">
                    <input
                        type="text"
                        placeholder="Postcode"
                        value={postcode}
                        readOnly={true}
                    />
                    <input
                        type="text"
                        placeholder="City"
                        value={city && capitalizeFirstLetter(city)}
                        onChange={e => {
                            setError(null);
                            setCity(e.target.value);
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Address line 1"
                        onChange={e => {
                            setError(null);
                            setAddress1(e.target.value);
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Address line 2"
                        onChange={e => {
                            setError(null);
                            setAddress2(e.target.value);
                        }}
                    />
                    <div className="action-wrapper">
                        <span id="saveAddress" onClick={handleSave}>
                            Save
                        </span>
                        <span onClick={clearState}>Cancel</span>
                    </div>
                    {error !== null && <ErrorMessage message={error} />}
                </div>
            );
        }

        return renderDefaultInput(hasPostcodeResults);
    };

    return (
        <div className={classNames('card LocationCard', className)}>
            {inputOnly ? (
                renderAddressSelector()
            ) : (
                <div className="card-content">
                    <p className="card-title">
                        {`${user.isAgent ? 'Venue & ' : ''}Location`}
                    </p>
                    <section>
                        <div className="map-wrapper">
                            <div className="map" />
                        </div>
                        {renderAddressSelector()}
                    </section>
                </div>
            )}
        </div>
    );
}
